import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	HeadContent,
	Scripts,
} from "@tanstack/react-router";
import type { TRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { PageAnalytics } from "#/components/page-analytics";
import { SplashScreen } from "#/components/splash-screen";
import { env } from "#/env";
import type { TRPCRouter } from "#/integrations/trpc/router";
import { ADMIN_THEME_BOOT_SCRIPT } from "#/lib/admin-theme";
import { I18nProvider, readLocaleCookie, useI18n } from "#/lib/i18n";
import appCss from "../styles.css?url";

interface MyRouterContext {
	queryClient: QueryClient;

	trpc: TRPCOptionsProxy<TRPCRouter>;
}

const SITE_NAME = "أكاديمية مستشير";
const DEFAULT_TITLE = "دورة إنشاء المؤسسات في الجزائر | أكاديمية مستشير";
const DEFAULT_DESCRIPTION =
	"دورة تطبيقية مكثفة (18 ساعة) لتحويل فكرة مشروعك إلى مؤسسة تنافسية ومستدامة: الجانب القانوني، الجبائي، والتسويقي لإنشاء مؤسستك في الجزائر.";

export const Route = createRootRouteWithContext<MyRouterContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			{
				title: DEFAULT_TITLE,
			},
			{
				name: "description",
				content: DEFAULT_DESCRIPTION,
			},
			{
				property: "og:site_name",
				content: SITE_NAME,
			},
			{
				property: "og:type",
				content: "website",
			},
			{
				property: "og:title",
				content: DEFAULT_TITLE,
			},
			{
				property: "og:description",
				content: DEFAULT_DESCRIPTION,
			},
			{
				property: "og:locale",
				content: "ar_DZ",
			},
			{
				name: "twitter:card",
				content: "summary_large_image",
			},
			{
				name: "theme-color",
				content: "#0a1526",
			},
		],
		links: [
			{
				// Deliberately a plain, render-blocking stylesheet. The usual
				// `media="print"` + `onload="this.media='all'"` trick does not work
				// here: TanStack renders link attrs through JSX, so React rejects the
				// lowercase `onload` prop ("Invalid event handler property") and drops
				// it — leaving the sheet stuck on media="print" and the whole site
				// unstyled. It also isn't worth doing: this is the only stylesheet,
				// it paints everything above the fold (so nothing meaningful can
				// render before it anyway), and it's ~13 KB brotli — about one round
				// trip. Deferring it buys no real LCP, it only moves the wait.
				rel: "stylesheet",
				href: appCss,
			},
			{
				rel: "icon",
				href: "/favicon.svg",
				type: "image/svg+xml",
			},
			{
				rel: "icon",
				href: "/favicon.ico",
				sizes: "48x48",
			},
			{
				rel: "apple-touch-icon",
				href: "/logo192.png",
			},
			{
				rel: "manifest",
				href: "/manifest.json",
			},
			{
				// The @font-face rules themselves are inlined into our own
				// stylesheet now (see src/font-faces.css) — the actual .woff2
				// files still come from here, so this stays.
				rel: "preconnect",
				href: "https://fonts.gstatic.com",
				crossOrigin: "anonymous",
			},
		],
		// GA loads on every route so site-wide analytics stay in one property.
		// Meta Pixel is per-training now (see src/routes/training/$slug.tsx) —
		// each training's own pixel ID is only injected on its own landing route.
		scripts: [
			...(env.VITE_GA_MEASUREMENT_ID
				? [
						{
							src: `https://www.googletagmanager.com/gtag/js?id=${env.VITE_GA_MEASUREMENT_ID}`,
							async: true,
						},
						{
							children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${env.VITE_GA_MEASUREMENT_ID}',{send_page_view:true});`,
						},
					]
				: []),
		],
	}),
	shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
	// Read once (isomorphic: request cookie on the server, document cookie on the
	// client) so the very first paint already matches the chosen language and no
	// hydration mismatch or RTL/LTR flash occurs.
	const initialLocale = readLocaleCookie();
	return (
		<I18nProvider initialLocale={initialLocale}>
			<Document>{children}</Document>
		</I18nProvider>
	);
}

function Document({ children }: { children: React.ReactNode }) {
	const { locale, dir } = useI18n();
	return (
		// suppressHydrationWarning: the admin theme script below may add
		// data-admin-theme to <html> before React hydrates it.
		<html lang={locale} dir={dir} suppressHydrationWarning>
			<head>
				{/* First, so an admin page in dark mode never paints light first. A
				    no-op on every public page. */}
				<script
					// biome-ignore lint/security/noDangerouslySetInnerHtml: a constant from our own code, no user input
					dangerouslySetInnerHTML={{ __html: ADMIN_THEME_BOOT_SCRIPT }}
				/>
				<HeadContent />
			</head>
			<body className="font-sans bg-canvas text-ink antialiased">
				<SplashScreen />
				{children}
				<PageAnalytics />
				{/* TanStack Devtools disabled — not used day-to-day, and keeping it
				out avoids shipping the extra JS to production entirely. */}
				<Scripts />
			</body>
		</html>
	);
}
